<!-- Action toolbar: same icon-only style as the Rules tab's toolbar (see
     rules.component.html), kept separate from the search controls below so
     it reads as one row of actions with no vertical separators between them. -->
<p-toolbar styleClass="results-actions-toolbar">
  <div class="p-toolbar-group-start align-items-center flex-wrap gap-1">
    <p-button icon="pi pi-filter-slash" size="small" [text]="true"
              pTooltip="Clear filters" tooltipPosition="bottom"
              (onClick)="clear()">
    </p-button>
    <!-- Mirrors the Rules tab's Mine/All toggle exactly - see rules.component.html. -->
    <p-toggleButton [ngModel]="scope === 'all'" (onChange)="setScope($event.checked ? 'all' : 'mine')"
                    onIcon="pi pi-users" offIcon="pi pi-user"
                    [ariaLabel]="scope === 'all'
                      ? 'Showing results for all users' : 'Showing only your results'"
                    pTooltip="Switch between mine or all" tooltipPosition="bottom"
                    styleClass="p-button-sm">
    </p-toggleButton>
    <p-button icon="pi pi-refresh" size="small" [text]="true" [loading]="loading"
              pTooltip="Refresh results" tooltipPosition="bottom"
              (onClick)="search()">
    </p-button>
    <p-button icon="pi pi-sitemap" size="small" [text]="true"
              [disabled]="!selectedRow"
              pTooltip="Show in Files Tab" tooltipPosition="bottom"
              (onClick)="openInTree()">
    </p-button>
  </div>
</p-toolbar>

<p-toolbar styleClass="results-toolbar">
  <div class="p-toolbar-group-start align-items-center flex-wrap gap-2">
    <input pInputText type="text" placeholder="File name" [(ngModel)]="fileName"
           (keydown.enter)="search()" class="p-inputtext-sm results-filename"/>
    <p-dropdown [options]="toolOptions" [(ngModel)]="tool"
                optionLabel="label" optionValue="value"
                placeholder="Any tool" [showClear]="true"
                styleClass="p-inputtext-sm" appendTo="body">
    </p-dropdown>
    <p-calendar [(ngModel)]="from" placeholder="From" dateFormat="yy-mm-dd"
                [showIcon]="true" [showButtonBar]="true" appendTo="body"
                styleClass="results-date">
    </p-calendar>
    <p-calendar [(ngModel)]="to" placeholder="To" dateFormat="yy-mm-dd"
                [showIcon]="true" [showButtonBar]="true" appendTo="body"
                styleClass="results-date">
    </p-calendar>
    <p-button label="Search" size="small" (onClick)="search()"></p-button>
  </div>
</p-toolbar>

<!-- Same four actions as the toolbar above (Clear/Mine-All/Refresh/Show in
     Files Tab), reachable by right-clicking anywhere in the table - not
     per-row actions, so it doesn't change/require a row selection itself. -->
<p-contextMenu #cm [model]="rowMenuItems"></p-contextMenu>

<!-- results-table-wrapper: PrimeNG's own [loading]/[showLoader] overlay is a
     child of the table's OWN root box, which spans the header too (its
     <thead> is merely `position: sticky`, not a separate box) - so turning
     it on covered the column headers along with the rows, which is why it
     was turned off (showLoader="false") when this table was first built.
     This wrapper's overlay sits alongside <p-table>, not inside it, so it
     covers this exact same box - but at z-index 1, one below the sticky
     header's z-index 2 (see PrimeNG's own .p-datatable-scrollable-table>
     .p-datatable-thead rule), so the header paints on top of it and stays
     readable/usable while only the rows beneath are dimmed. -->
<div class="results-table-wrapper">
  <p-table [value]="rows" [loading]="loading" [showLoader]="false"
           styleClass="p-datatable-striped p-datatable-sm results-table"
           [scrollable]="true" scrollHeight="flex"
           [resizableColumns]="true" columnResizeMode="expand"
           (contextmenu)="refreshRowMenuItems(); cm.show($event)">
    <ng-template pTemplate="header">
      <tr>
        <th class="results-icon-col"></th>
        <th class="results-filename-col" pResizableColumn [style.width.rem]="11">File</th>
        <th pResizableColumn [style.width.rem]="8">Tool</th>
        <th pResizableColumn [style.width.rem]="9">Run Date</th>
        <th *ngIf="scope === 'all'" pResizableColumn [style.width.rem]="9">Owner</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-row let-rowIndex="rowIndex">
      <tr class="results-row" [class.p-highlight]="row === selectedRow"
          tabindex="0" role="button" [attr.aria-label]="'Open ' + row.fileName"
          (click)="openRow(row)"
          (keydown.enter)="openRow(row)"
          (keydown.space)="openRow(row); $event.preventDefault()">
        <td><i class="pi" [ngClass]="iconFor(row)" role="img" aria-label="Result type"></i></td>
        <td class="results-filename-col" [pTooltip]="fileTooltip(row)" tooltipPosition="top">
          {{row.fileName}}
          <!-- Same size display as the Files tab tree node (file-tree.component.html). -->
          <span class="text-xs font-italic text-primary">{{ row.size | filesize }}</span>
        </td>
        <td>{{toolName(row)}}</td>
        <td>{{row.runDate | date:'short'}}</td>
        <td *ngIf="scope === 'all'">{{row.userName}}</td>
      </tr>
      <!-- A row (not a sibling element below the table) so it's always
           reachable via the table's own scrolling, regardless of the outer
           panel's height/layout. -->
      <tr *ngIf="hasMore && rowIndex === rows.length - 1"
          class="results-load-more-row" tabindex="0" role="button" aria-label="Load more results"
          (click)="loadMore()"
          (keydown.enter)="loadMore()"
          (keydown.space)="loadMore(); $event.preventDefault()">
        <td [attr.colspan]="scope === 'all' ? 5 : 4" class="text-center">
          <em>{{ loading ? 'Loading more…' : 'Load more…' }}</em>
        </td>
      </tr>
    </ng-template>
    <ng-template pTemplate="emptymessage">
      <tr>
        <td [attr.colspan]="scope === 'all' ? 5 : 4" class="text-center text-color-secondary">
          No results found.
        </td>
      </tr>
    </ng-template>
  </p-table>
  <div class="results-loading-overlay" *ngIf="loading">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
  </div>
</div>
